import React from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { select } from 'd3-selection';
import { transition } from 'd3-transition';
import { interpolate } from 'd3-interpolate';
import styles from './bubble-chart.scss';
import get from 'lodash/get';
import * as selectors from '../../reducers';
import { camelCase } from 'lodash';
import { getBubbleScale, getBubbleColor } from '../../modules/bubbleHelper';
import { isIOS } from '../../modules/navigatorHelper';
import { setActiveProject, confirmTabChange } from '../../actions/index';
import boundingBoxClientRect from 'bounding-client-rect';
import DefPatterns from './defs-patterns';
import { setupForce, setCirclePosFromForce } from './force-helpers';
import ChartTooltip from './chart-tooltip';
import { gaTracking } from '../../modules/ga';
import './animations.scss';

class BubbleChart extends React.PureComponent {
  constructor() {
    super();
    this.state = {
      tooltipHover: false,
    };
    this.handleTooltipMouseOver = this.handleTooltipMouseOver.bind(this);
    this.handleTooltipMouseLeave = this.handleTooltipMouseLeave.bind(this);
  }
  componentDidMount() {
    this.drawChart({ didMount: true });
  }

  componentDidUpdate(prevProps, prevState) {
    this.ensurePopupIsInCorrectState(this.selection);
    const { tabChangeRequested } = this.props;
    if (tabChangeRequested) {
      this.clearChart();
    }

    this.drawChart();
  }

  componentWillUnmount() {
    select(this.refs.circleContainer)
      .selectAll('circle')
      .transition();
    this.refs.circleContainer.innerHtml = '';
    clearTimeout(this.tabChangeTimeout);
  }

  setIsReadyToLeave() {
    this.props.setIsReadyToLeave(true);
  }

  clearChart() {
    const { confirmTabChange } = this.props;
    this.tabChangeTimeout = setTimeout(confirmTabChange, 500);
    select(this.refs.circleContainer)
      .selectAll('circle')
      .transition()
      .duration(300)
      .attr('r', 0);
  }

  handleTooltipMouseOver() {
    this.setState({ tooltipHover: true });
  }

  handleTooltipMouseLeave() {
    this.setState({ tooltipHover: false });
  }

  chartTooltipRenderer(eventType, project, circleNode) {
    if (eventType === 'mouseenter' && this.props.activeProject) return;
    ReactDOM.render(
      <ChartTooltip
        project={project}
        circleNode={circleNode}
        canvasId={this.props.canvasId}
        tab={this.props.activeTab}
        onTooltipOver={this.handleTooltipMouseOver}
        onTooltipLeave={this.handleTooltipMouseLeave}
      />,
      this.refs.chartTooltip
    );
  }

  setSvgWithAndHeightBasedOnForce(svg, circleContainer) {
    const bounds = boundingBoxClientRect(circleContainer.node());
    const widthOrHeight = Math.max(bounds.width, bounds.height) + 40;
    svg.attr('width', widthOrHeight);
    svg.attr('height', widthOrHeight);
    svg
      .select('g')
      .attr(
        'transform',
        `translate(${widthOrHeight / 2},${widthOrHeight / 2})`
      );
  }

  ensurePopupIsInCorrectState(selection) {
    const { activeProject } = this.props;
    const activeProjectSelection = selection.filter(d => {
      return d.id === get(activeProject, 'id');
    });

    if (activeProjectSelection.size()) {
      // If we have an active project make sure the popup is showing.
      this.chartTooltipRenderer(
        'no-event',
        activeProjectSelection.data()[0],
        activeProjectSelection.node()
      );
    } else {
      // If we dont have an active project hide the tool tip
      this.chartTooltipRenderer(null);
    }
  }

  drawChart({ didMount } = {}) {
    const { budgets, activeProject, data, setActiveProject } = this.props;
    const rScale = getBubbleScale(budgets);
    const chartTooltipRenderer = this.chartTooltipRenderer.bind(this);

    const svg = select(this.refs.svg);
    const circleContainer = select(this.refs.circleContainer);
    const node = circleContainer.selectAll('circle').data(data);

    const simulation = setupForce(rScale);

    node
      .exit()
      .transition()
      .remove();

    const nodeEnter = node
      .enter()
      .append('circle')
      .attr('id', d => {
        return 'project' + d.id;
      })
      .attr('r', function(d) {
        if (!d.budget) return 10;
        return rScale(d.budget);
      })
      .style('fill', 'transparent')
      .on('click', function(d) {
        setActiveProject(d);
        chartTooltipRenderer('click', d, select(this).node());
        gaTracking('Project click', {
          event_label: d.name,
        });
      })
      .on('mouseenter', function(d) {
        if (isIOS) {
          if (activeProject) return;
          setActiveProject(d);
          chartTooltipRenderer('click', d, select(this).node());
          gaTracking('Project click', {
            event_label: d.name,
          });
        } else {
          chartTooltipRenderer('mouseenter', d, select(this).node());
        }
      })
      .on('mouseleave', _ => {
        if (this.props.activeProject || this.state.tooltipHover) return;
        chartTooltipRenderer('mouseleave', null);
      });

    setCirclePosFromForce(nodeEnter, simulation, data);

    simulation.on('end', _ => {
      simulation.stop();
      this.setSvgWithAndHeightBasedOnForce(svg, circleContainer);
      this.selection = didMount ? nodeEnter : node;
      if (didMount) {
        circleContainer
          .selectAll('circle')
          .transition()
          .duration(300)
          .style('fill', getBubbleColor)
          .attrTween('r', function(d) {
            let radius = d.budget ? rScale(d.budget) : 10;
            var i = interpolate(0, radius);
            return function(t) {
              return (radius = i(t));
            };
          })
          .on('end', () => {
            this.ensurePopupIsInCorrectState(
              this.selection,
              chartTooltipRenderer
            );
          });
      }
    });
  }

  render() {
    const { title, tabChangeRequested } = this.props;
    return (
      <div>
        <div
          className={`${styles.chartWrapper} ${tabChangeRequested
            ? styles.chartWrapperLeaving
            : ''}`}
          style={{ opacity: '1' }}
          id={'cluster' + camelCase(title)}
          onMouseLeave={this.handleTooltipMouseLeave}
        >
          <svg width={500} height={500} ref="svg">
            <DefPatterns />
            <g
              className={'circleContainer' + camelCase(title)}
              ref="circleContainer"
            />
          </svg>
        </div>
        <div className="chartTooltip" ref="chartTooltip" />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  budgets: selectors.getBudgets(state),
  activeProject: selectors.getActiveProject(state),
  tabChangeRequested: selectors.tabChangeRequested(state),
  activeTab: selectors.getActiveTab(state),
});

const mapDispatchToProps = {
  setActiveProject,
  confirmTabChange,
};

BubbleChart.propTypes = {
  title: PropTypes.string,
  data: PropTypes.array.isRequired,
  setActiveProject: PropTypes.func.isRequired,
  confirmTabChange: PropTypes.func.isRequired,
  budgets: PropTypes.array,
  activeProject: PropTypes.object,
  canvasId: PropTypes.string,
  tabChangeRequested: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(BubbleChart);



// WEBPACK FOOTER //
// ./src/components/bubble-chart/bubble-chart.js